import { useBreakpointValue } from '@wegroup/design-system';
import React from 'react';
import { Enterprise } from '../../../../../../../types/ProductFactory';
import ErrorPage from '../../../../../../common/src/components/ErrorPage';
import CompanyRegistrationConfirmation from './CompanyRegistrationConfirmation';
import CompanyRegistrationData from './CompanyRegistrationData';
import CompanyRegistrationLanguage from './CompanyRegistrationLanguage';
import CompanyRegistrationSearch from './CompanyRegistrationSearch';
import CompanyRegistrationAddressSearch from './CompanyRegistrationAddressSearch';

import { useTranslation } from 'react-i18next';
import cogoToast from 'cogo-toast';
import { isEmpty } from 'lodash';
import {
  FlowsQuestion,
  FlowsQuestionWithNewEnum,
} from '../../../../types/FlowsTypes';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import useSubView from '../../../../hooks/useSubView';
import useFlowQuestions from '../../../../hooks/useFlowQuestions';
import { useFlowsProgression } from '../../../../hooks/useFlowsProgression';
import { useFlowsSelector } from '../../../../../../common/src/utils/globalVariables';
import { useAuth } from '../../../../../../app/src/features/authentication/hooks/useAuth';
import defaultLanguageQuestion from '../../../ui/advisory-flow/constants/DefaultLanguageQuestion.json';

interface Props {
  question: FlowsQuestion;
}

const CompanyRegistrationInput: React.FC<React.PropsWithChildren<Props>> = ({
  question,
}) => {
  const { t } = useTranslation();
  const questions = useFlowQuestions();
  const { distribution } = useAuth();

  // Get country_code from flow info or distribution
  const flowCountryCode = useFlowsSelector(
    (state) => state.flows.flowsResponse?.info.country_code,
  );
  const countryCode =
    flowCountryCode || distribution?.address?.country_code || 'BE';

  const {
    field: { value, onBlur },
  } = useFlowInputComponent({
    question,
  });

  const { nextQuestion, previousQuestion } = useFlowsProgression();

  const { currentSubView, nextSubView, previousSubView } = useSubView({
    question,
    defaultView: 'companyRegistrationSearch',
  });

  const isMobile = useBreakpointValue({
    base: true,
    laptop: false,
  });

  const legalLanguageQuestion =
    questions.find(({ id }) => id === 'company.language') ||
    questions.find(({ id }) => id === 'policy_holder.language') ||
    (defaultLanguageQuestion as FlowsQuestionWithNewEnum);

  const company = (value as { _local: Enterprise })?._local;
  const language = (value as { 'company.language': string })?.[
    'company.language'
  ];

  const handleOnChange = (arg0: Enterprise | boolean) => {
    const nextValue: Record<string, unknown> = {
      ...(value as Record<string, unknown>),
    };
    delete nextValue['company.name'];
    delete nextValue['company.cbe'];
    delete nextValue['company.registration_nr'];
    delete nextValue['company.address'];
    nextValue._local = arg0 as unknown as Enterprise | boolean;
    onBlur({ name: question.id, value: nextValue });
  };

  const handleLanguageChange = (language: string) => {
    if (language) {
      onBlur({
        name: question.id,
        value: {
          ...(value as Record<string, unknown>),
          'company.language': language,
        },
      });
    }
  };

  const onContinue = (values: Enterprise) => {
    const existingLanguage = (value as Record<string, unknown>)?.[
      'company.language'
    ];
    onBlur({
      name: question.id,
      value: {
        ...(values as unknown as Record<string, unknown>),
        ...(existingLanguage ? { 'company.language': existingLanguage } : {}),
        _local: values,
      },
    });

    // Skip the language selection step for company registration
    nextQuestion();
  };

  const handleNextView = () => {
    // checks if address is valid - more lenient validation for company addresses
    const isAddressValid = (address: unknown) => {
      if (!address || typeof address !== 'object') return false;
      const addr = address as Record<string, unknown>;

      // Check if we have at least street and city (most essential fields)
      const hasStreet =
        addr.street &&
        typeof addr.street === 'string' &&
        addr.street.trim() !== '';
      const hasCity =
        addr.city && typeof addr.city === 'string' && addr.city.trim() !== '';

      return hasStreet && hasCity;
    };

    if (isAddressValid(company.address)) {
      nextSubView('companyRegistrationConfirmation');
    } else {
      cogoToast.info(
        t(
          'Oops there is some data missing. Please fill in the empty required fields',
        ),
        { hideAfter: 7 },
      );
      nextSubView('companyRegistrationData');
    }
  };

  const handleOnCantFindCompany = () => {
    onBlur({ name: 'company.company_search', value: { _local: {} } });
    nextSubView('companyRegistrationData');
  };

  return (
    <>
      {{
        companyRegistrationSearch:
          (question.rules.options?.company_search_by || 'QUERY') ===
          'ADDRESS' ? (
            <CompanyRegistrationAddressSearch
              previousQuestionHandler={previousQuestion}
              nextSubView={handleNextView}
              onCantFind={handleOnCantFindCompany}
              handleOnChange={handleOnChange}
              hasValue={!isEmpty(company)}
              countryCode={countryCode}
            />
          ) : (
            <CompanyRegistrationSearch
              previousQuestionHandler={previousQuestion}
              nextSubView={handleNextView}
              onCantFind={handleOnCantFindCompany}
              handleOnChange={handleOnChange}
              hasValue={!isEmpty(company)}
              countryCode={countryCode}
            />
          ),
        companyRegistrationConfirmation: (
          <CompanyRegistrationConfirmation
            isMobile={isMobile}
            company={company}
            previousSubView={previousSubView}
            nextQuestionHandler={() => onContinue(company)}
            nextSubView={nextSubView}
          />
        ),
        companyRegistrationData: (
          <CompanyRegistrationData
            isMobile={isMobile}
            company={company}
            previousSubView={previousSubView}
            nextQuestionHandler={onContinue}
            handleOnChange={handleOnChange}
          />
        ),
        companyRegistrationLanguage: (
          <CompanyRegistrationLanguage
            isMobile={isMobile}
            language={language}
            previousSubView={previousSubView}
            nextQuestionHandler={nextQuestion}
            handleChange={handleLanguageChange}
            legalLanguageQuestion={legalLanguageQuestion}
          />
        ),
      }[currentSubView] || <ErrorPage />}
    </>
  );
};

export default CompanyRegistrationInput;
